import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button as Button, F0TagStatus, F0Text, ProgressBarChart } from '@factorialco/f0-react'
import {
  CreateBlockProgressDocument,
  LoadContentDocument,
  LogTrainingContentEntryDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { LMSModuleAvailabilityWall } from 'modules/trainings/components/LMSModuleAvailabilityWall'
import ContentConsumer from 'modules/trainings/components/content/Consumer'
import { buildTableOfContentItems } from 'modules/trainings/components/content/helpers/buildTableOfContentItems'
import {
  type FormMembership,
  FormMembershipProvider,
} from 'modules/trainings/components/content/FormMembershipContext'
import { ContentConsumerCallbacks } from 'modules/trainings/components/content/types'
import { useTrainingContentExitLogger } from 'modules/trainings/hooks/useTrainingContentExitLogger'
import { Block } from 'modules/trainings/lib/content/Block'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { QuizBlock } from 'modules/trainings/lib/content/QuizBlock'
import { graphqlContentMapper } from 'modules/trainings/lib/content/mappers'

const showError = () => app.setFlash({ type: 'wrong', message: i18n.t('common.error') })

export default observer(() => {
  const { trainingId, blockId } = useParams()
  const { company, employee: currentEmployee, access } = useCurrentSession()
  const navigate = useNavigate()
  const consumerRef = useRef<ContentConsumerCallbacks>(null)
  const [showEmptyState, setShowEmptyState] = useState(false)
  const { navigation } = useNavigation()
  const { data, loading } = useQuery(LoadContentDocument, {
    variables: {
      companyId: company?.id,
      withFF: true,
      trainingId: Number(trainingId),
      trainingIdString: trainingId?.toString(),
      employeeId: currentEmployee.id,
      respondentId: access?.id?.toString() || '',
      skipFormMemberships: !access?.id, // Only fetch if we have respondentId
    },
    skip: !access?.id,
  })

  const modules = data?.trainings.contentModules
  const collection = useMemo(() => {
    if (loading || !modules) return null

    const newCollection = graphqlContentMapper.fromGraphQL(modules, Number(trainingId))

    if (blockId) {
      const block = newCollection.flatItems.find(
        (item) => item instanceof Block && item.id === Number(blockId)
      )
      if (block) {
        newCollection.setSelected(block.uuid)
      }
    } else {
      const allBlocks = newCollection.flatItems.filter(
        (item): item is Block => item instanceof Block
      )
      const courseNotStarted = allBlocks.every((b) => b.status === 'notstarted')

      if (courseNotStarted && newCollection.modules[0]) {
        newCollection.setSelected(newCollection.modules[0].uuid)
      } else {
        const firstIncomplete = allBlocks.find((b) => b.accesible && b.status !== 'completed')
        if (firstIncomplete) {
          newCollection.setSelected(firstIncomplete.uuid)
        }
      }
    }

    return newCollection
  }, [modules, loading, trainingId, blockId])

  const [createBlockProgress] = useMutation(CreateBlockProgressDocument, {
    extractorFn: (mutationData) =>
      mutationData.trainingsMutations.createTrainingsContentBlockProgress,
  })

  const isDone = collection
    ? collection.modules.every((m) => m.blocks.every((b) => b.status === 'completed'))
    : false

  const nonModuleItems =
    collection?.flatItems.filter(
      (item) => !(item instanceof ContentModule) && !(item instanceof QuizBlock && !item.form)
    ) ?? []

  const completedCount = nonModuleItems.filter(
    (item) => item instanceof Block && item.status === 'completed'
  ).length

  const totalItems = nonModuleItems.length
  const percentage = totalItems > 0 ? Math.round((completedCount / totalItems) * 100) : 0

  let currentPosition = 0
  if (showEmptyState) {
    currentPosition = totalItems
  } else if (collection?.selectedItem) {
    const blockIndex = nonModuleItems.findIndex(
      (item) => item.uuid === collection.selectedItem?.uuid
    )
    if (blockIndex >= 0) {
      currentPosition = blockIndex + 1
    } else {
      const flatIndex = collection.flatItems.findIndex(
        (item) => item.uuid === collection.selectedItem?.uuid
      )
      if (flatIndex > 0) {
        for (let i = flatIndex - 1; i >= 0; i--) {
          const item = collection.flatItems[i]
          const blockPosition = nonModuleItems.findIndex((b) => b.uuid === item?.uuid)
          if (blockPosition >= 0) {
            currentPosition = blockPosition + 1
            break
          }
        }
      }
    }
  }

  const isBlockCompleted =
    collection?.selectedItem instanceof Block && collection.selectedItem.status === 'completed'

  const isFirstModule = collection?.selectedItem === collection?.modules[0]

  useEffect(() => {
    if (isDone && collection && !blockId) {
      collection.setSelected(undefined)
      setShowEmptyState(true)
    }
  }, [isDone, collection, blockId])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate(-1)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  const advanceToNext = useCallback(() => {
    if (!collection) return

    const items = collection.flatItems
    const currentIndex = items.findIndex((item) => item.uuid === collection.selectedItem?.uuid)
    const shouldSkip = (item: (typeof items)[number]) => item instanceof QuizBlock && !item.form

    for (let i = currentIndex + 1; i < items.length; i++) {
      const item = items[i]
      if (item && !shouldSkip(item)) {
        if (item instanceof Block) item.accesible = true
        collection.setSelected(item.uuid)
        return
      }
    }

    collection.setSelected(undefined)
    setShowEmptyState(true)
  }, [collection])

  const handleNext = useCallback(async () => {
    if (!collection) return

    if (collection.selectedItem instanceof ContentModule) {
      collection.next()
      return
    }

    if (!(collection.selectedItem instanceof Block)) return

    const currentBlock = collection.selectedItem

    try {
      const shouldAdvance = await consumerRef.current?.handleNext()

      if (shouldAdvance === false) {
        return
      }

      const result = await createBlockProgress({
        variables: {
          contentBlockId: currentBlock.id,
          employeeId: currentEmployee.id,
        },
      })

      const handled = handleResult(result, showError)

      if (handled.success) {
        currentBlock.status = 'completed'
        currentBlock.accesible = true
        advanceToNext()
      }
    } catch {
      showError()
    }
  }, [collection, createBlockProgress, currentEmployee.id, advanceToNext])

  const handleRestart = useCallback(() => {
    const firstItem = collection?.flatItems[0]
    if (firstItem) {
      collection?.setSelected(firstItem.uuid)
      setShowEmptyState(false)
    }
  }, [collection])

  const formMemberships = useMemo<FormMembership[]>(() => {
    const nodes = data?.forms?.membershipsConnection?.nodes || []
    return nodes.map((node) => ({
      id: node.id,
      formId: node.materializedForm?.form?.id || '',
      answers: node.answers || [],
      score: node.score ? Number(node.score) : null,
    }))
  }, [data?.forms?.membershipsConnection?.nodes])

  const menuItems = useMemo(
    () => (collection ? buildTableOfContentItems(collection, { filterEmptyQuizBlocks: true }) : []),
    [collection, collection?.selectedItem, completedCount]
  )

  const { has_dev_training_lms_connectivity_logs } = checkFeatures(
    ['dev_training_lms_connectivity_logs'],
    data?.features?.featuresConnection?.nodes
  )

  // Setup exit logging for all navigation scenarios (close tab, back button, etc.)
  const { logExit } = useTrainingContentExitLogger({
    trainingId,
    enabled: has_dev_training_lms_connectivity_logs,
  })

  const [logTrainingContentEntry] = useMutation(LogTrainingContentEntryDocument, {
    extractorFn: (data) => data.trainingsMutations.logEntryTrainingsContentActivityLog,
  })

  // Track if entry has been logged for current session to prevent duplicate logs
  const hasLoggedEntry = useRef(false)

  // Log entry when component mounts or when trainingId changes
  // This handles: direct URL access, back button navigation, and URL parameter changes
  useEffect(() => {
    // Reset the flag when trainingId changes (new training or navigation)
    hasLoggedEntry.current = false
  }, [trainingId])

  useEffect(() => {
    const logEntry = async () => {
      // Only log if:
      // 1. Feature flag is enabled
      // 2. TrainingId exists
      // 3. Haven't logged entry yet for this session
      if (has_dev_training_lms_connectivity_logs && trainingId && !hasLoggedEntry.current) {
        try {
          const result = await logTrainingContentEntry({
            variables: {
              trainingId: Number(trainingId),
              employeeId: currentEmployee.id,
            },
          })

          const handled = handleResult(result, showError)
          if (handled.success) {
            hasLoggedEntry.current = true
          }
        } catch {
          showError()
        }
      }
    }

    logEntry()
  }, [
    has_dev_training_lms_connectivity_logs,
    trainingId,
    currentEmployee.id,
    logTrainingContentEntry,
  ])

  if (!collection || loading) return <Loading expand />

  const content = (
    <>
      <LMSModuleAvailabilityWall>
        <FormMembershipProvider memberships={formMemberships}>
          <Box
            position="fixed"
            width="full"
            height="full"
            background="grey000White"
            style={{ zIndex: '999', top: '0', left: '0' }}
          >
            <Box
              position="fixed"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
              padding="s16"
              paddingX="s24"
              background="grey000White"
              height="s64"
              style={{ top: '0', left: '0', right: '0', zIndex: '11' }}
            >
              <Box flexDirection="row" alignItems="center" gap="s16">
                <Box width="s200" gap="s8" flexDirection="row" alignItems="center">
                  <F0Text
                    content={i18n.t('trainings.content.consumer.progress')}
                    variant="description"
                  />
                  <Box width="full">
                    <ProgressBarChart
                      value={completedCount}
                      max={totalItems}
                      label={`${percentage}%`}
                      color="feedback-positive"
                    />
                  </Box>
                </Box>

                {isBlockCompleted && (
                  <F0TagStatus
                    text={i18n.t('trainings.content.syllabus.status.completed')}
                    variant="positive"
                  />
                )}
              </Box>

              <Box flexDirection="row" alignItems="center" gap="s12">
                <F0Text content={`${currentPosition}/${totalItems}`} variant="description" />
                <F0Text content="|" variant="small" />
                <Button
                  label={i18n.t('trainings.content.consumer.exit')}
                  variant="outline"
                  onClick={async () => {
                    // Use hook's logExit for controlled async exit
                    await logExit()
                    navigate(
                      navigation.myTrainings.revamp.detailsRevamp.view.contentOverview.fullPath({
                        trainingId: trainingId ?? '',
                      })
                    )
                  }}
                />
              </Box>
            </Box>

            <Box
              flexDirection="column"
              height="full"
              flexGrow
              overflowY="auto"
              padding="s4"
              paddingTop="s64"
            >
              <ContentConsumer
                selectedItem={collection.selectedItem}
                ref={consumerRef}
                showEmptyState={showEmptyState}
                menuItems={menuItems}
                onBack={isFirstModule ? undefined : () => collection.back()}
                onNext={handleNext}
                onRestart={handleRestart}
              />
            </Box>
          </Box>
        </FormMembershipProvider>
      </LMSModuleAvailabilityWall>
    </>
  )

  if (typeof document === 'undefined') return content

  const mainElement = document.querySelector('main')
  return mainElement ? createPortal(content, mainElement) : content
})
